import { ClientInterface, PromotionInterface } from '@e3labs/isa-data-dictionary';
import { AxiosResponse } from 'axios';
import { calcPercDiscount } from '~/utils/calcPercDiscount';
import { toDecimals } from '~/utils/format';
import api from '../../../services/api';
import { ProductCustomInterface } from '../order/order-interface';
import { AvailablePromotionsParamsProps, RequiredPromotionProduct } from './promotion-interface';

export interface PromotionOrderInterface extends PromotionInterface {
  active?: Boolean;
  minimumPrice?: number;
  promotionalPrice?: number;
  soldPrice?: number;
}
class PromotionService {
  getAllRegisteredPromotions({ skip = 0, limit = 2000 }) {
    return api.get('/promotion/group', {
      params: {
        skip,
        limit
      }
    });
  }

  getRegisteredPromotion(id: any) {
    return api.get(`/promotion/group/${id}`);
  }

  getRegisteredPromotionCount() {
    return api.get('/promotion/group/count');
  }

  getAllImportedPromotions({ skip = 0, limit = 2000 }) {
    return api.get('/promotion', {
      params: {
        skip,
        limit
      }
    });
  }
  getImportedPromotion(id: any) {
    return api.get(`/promotion/${id}`);
  }

  getImportedPromotionCount() {
    return api.get('/promotion/count');
  }

  getAvailablePromotions({
    client,
    product,
    subsidiaryExternalCode,
    paymentPlanExternalCode,
    priceTableCode,
    ...rest
  }: AvailablePromotionsParamsProps): Promise<AxiosResponse<PromotionInterface[] | null>> {
    return api.get('/promotion/available', {
      params: {
        subsidiaryExternalCode,
        priceTableCode,
        clientExternalCode: client.externalCode,
        clientActivityExternalCode: client.activityExternalCode,
        productExternalCode: product?.externalCode,
        categoryExternalCode: product?.categoryExternalCode,
        departmentExternalCode: product?.departmentExternalCode,
        brandExternalCode: product?.brandExternalCode,
        sectionExternalCode: product?.sectionExternalCode,
        productProviderExternalCode: product?.providerExternalCode,
        ...(client.usePaymentPlansGroup
          ? {
              productPaymentPlansGroup: product?.paymentPlansGroup
            }
          : {
              paymentPlanExternalCode
            }),
        productGroupExternalCodes: product?.productGroupExternalCodes?.join(','),
        ...rest
      }
    });
  }

  getSpecificsPromotions(params: AvailablePromotionsParamsProps): Promise<AxiosResponse<PromotionInterface[] | null>> {
    const {
      client,
      products,
      subsidiaryExternalCode,
      paymentPlanExternalCode,
      priceTableCode,
      orderType,
      loadType,
      shippingType
    } = params;

    const orderItems = products?.map(product => ({
      clientExternalCode: client.externalCode,
      productExternalCode: product?.externalCode,
      subsidiaryExternalCode,
      clientActivityExternalCode: client.activityExternalCode,
      priceTableCode,
      brandExternalCode: product?.brandExternalCode,
      categoryExternalCode: product?.categoryExternalCode,
      departmentExternalCode: product?.departmentExternalCode,
      sectionExternalCode: product?.sectionExternalCode,
      governmentalCode: client?.governmentalCode,
      productGroupExternalCodes: product?.productGroupExternalCodes?.join(','),
      productProviderExternalCode: product?.providerExternalCode,
      orderType,
      clientGroupExternalCode: client?.clientGroupExternalCode || undefined,
      loadType: loadType?.code,
      shippingType,
      ...(client.usePaymentPlansGroup
        ? {
            productPaymentPlansGroup: product?.paymentPlansGroup
          }
        : {
            paymentPlanExternalCode
          })
    }));

    return api.post('/promotion/products/available', orderItems);
  }

  deletePromotion(id: any) {
    return api.delete(`/promotion/group/${id}`);
  }
  createPromotion(payload: any): Promise<AxiosResponse<any | null>> {
    return api.post('/promotion/group', payload);
  }

  createPromotionImageKit({ file, promotionGroupExternalCode }) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('promotionGroupExternalCode', promotionGroupExternalCode);

    return api.put('/promotion/group/pictures', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  editPromotion(payload: any) {
    return api.put(`/promotion/group`, payload);
  }

  editPromotionImported(payload: any) {
    return api.put(`/promotion/`, payload);
  }

  createPromotionImageKitImported({ file, promotionExternalCode }) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('promotionExternalCode', promotionExternalCode);

    return api.put('/promotion/pictures', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  buildPromotion({ promotion, product, soldPrice, soldQuantity }: any | null, configuration: any) {
    if (!promotion) return null;

    let priceDifference = 0;

    //Price is inside or bellow promotion
    if (promotion?.typeBalance) {
      const difference = promotion?.promotionalPrice
        ? toDecimals(soldPrice - promotion?.promotionalPrice, configuration?.decimals ?? 2) * soldQuantity
        : toDecimals(
            soldPrice - (product?.baseValue - (product?.baseValue * promotion?.discountPercentage) / 100),
            configuration?.decimals ?? 2
          ) * soldQuantity;

      if (promotion?.creditBalance && difference > 0) {
        priceDifference = difference;
      } else if (promotion.debitBalance && difference < 0) {
        priceDifference = difference;
      }
    } else {
      priceDifference = (soldPrice - product?.baseValue) * soldQuantity;
    }

    return {
      priceDifference,
      promotion,
      promotionCode: promotion?.promotion?.code,
      promotionGroupCode: promotion?.promotionGroupCode,
      promotionName: promotion?.promotionName,
      promotionMedCode: promotion?.codpromocaomed,
      promotionExternalCode: promotion?.externalCode,
      checkingAccount: promotion?.checkingAccount,
      isPromotionGroup: this.isPromotionGroup(promotion),
      promotionalPrice: promotion?.promotionalPrice,
      batchCode: promotion.batchCode
    };
  }

  isPromotionGroup = (promotion?: PromotionOrderInterface | PromotionInterface | null | undefined): boolean => {
    if (!promotion) {
      return false;
    } else if (promotion?.brandExternalCode) {
      return true;
    } else if (promotion?.sectionExternalCode) {
      return true;
    } else if (promotion?.categoryExternalCode) {
      return true;
    } else if (promotion?.departmentExternalCode) {
      return true;
    } else if (promotion?.providerExternalCode) {
      return true;
    } else if (promotion?.productGroupExternalCode) {
      return true;
    } else {
      return false;
    }
  };

  async getPromotionByExternalCode({
    client,
    product,
    externalCode,
    soldPrice,
    configuration
  }: {
    client?: ClientInterface | null | undefined;
    product?: ProductCustomInterface | null | undefined;
    externalCode?: string | null | undefined;
    soldPrice?: number;
    configuration?: any;
  }): Promise<any> {
    try {
      if (!externalCode) {
        return null;
      }
      const { data: promotion } = await this.getImportedPromotion(externalCode);

      const _promotion: {
        priceDifference: number;
        promotion: any;
        promotionCode: any;
        promotionGroupCode: any;
        promotionName: any;
        promotionMedCode: any;
        promotionExternalCode: any;
        checkingAccount: any;
        isPromotionGroup: boolean;
        batchCode?: string;
      } = this.buildPromotion(
        {
          client,
          promotion,
          product,
          soldPrice: soldPrice ?? 0
        },
        configuration
      );

      return _promotion;
    } catch (error) {
      return null;
    }
  }

  buildPromotionSelected({
    promotion,
    client,
    product,
    soldPrice,
    forceActive = false,
    configuration
  }: {
    promotion: PromotionOrderInterface;
    client: ClientInterface;
    product: ProductCustomInterface;
    soldPrice: number;
    forceActive?: boolean;
    configuration: any;
  }): PromotionOrderInterface {
    const clientIsPJ = client?.governmentalCode?.length > 14 ? true : false;
    const tributacao = product?.tributacao ?? null;

    let addFixedPrice = 0;
    if (!clientIsPJ || (tributacao?.data?.aplicaacrescpjisenta && client?.finalConsumer)) {
      addFixedPrice =
        tributacao?.data?.aliqicms > 0
          ? toDecimals((product?.tabulatedPrice * tributacao?.data?.aliqicms) / 100, configuration?.decimals ?? 2)
          : 0;

      // tributacao?.data?.peracrescismopf > 0
      //   ? toDecimals(
      //       (product?.tabulatedPrice * tributacao?.data?.peracrescismopf) / 100,
      //       configuration?.decimals ?? 2
      //     )
      //   : 0;
    }

    const promotionName = this.getPromotionName(promotion);

    const discountPercentage = this.getDiscountPercentage(
      promotion,
      soldPrice,
      addFixedPrice,
      configuration.decimals ?? 2
    );
    const minimumPrice = this.getMinimumPrice(promotion, soldPrice, addFixedPrice, configuration.decimals ?? 2);

    const promotionalPrice = this.getPromotionPrice(promotion, soldPrice, addFixedPrice, configuration.decimals ?? 2);

    return {
      ...promotion,
      id: promotion._id,
      initialQuantity: promotion?.initialQuantity ?? 1,
      finalQuantity: promotion?.finalQuantity ?? 99999,
      minimumPromotionValue: promotion?.minimumPromotionValue ?? 0,
      soldPrice,
      promotionName,
      discountPercentage,
      minimumPrice,
      promotionalPrice,
      active: forceActive ?? promotion?.instantApplyDiscount
      // active: configuration?.allowGroupPromotions
      //   ? true
      //   : promotion?.instantApplyDiscount,
    };
  }

  private getMinimumPrice(promotion: PromotionOrderInterface, soldPrice: number, addFixedPrice = 0, precision = 2) {
    try {
      const discount =
        (promotion?.maxDiscountPercentage ? promotion?.maxDiscountPercentage : promotion?.discountPercentage) ?? 0;

      if (promotion?.fixedPrice) {
        return toDecimals(promotion?.fixedPrice + addFixedPrice, precision);
      } else {
        return toDecimals(discount === 0 ? soldPrice : soldPrice - (soldPrice * discount) / 100, precision);
      }
    } catch (error) {
      return 0;
    }
  }

  private getDiscountPercentage(
    promotion: PromotionOrderInterface,
    soldPrice: number,
    addFixedPrice = 0,
    precision = 2
  ) {
    try {
      return promotion?.fixedPrice
        ? calcPercDiscount(soldPrice, promotion?.fixedPrice + addFixedPrice, precision)
        : toDecimals(promotion?.discountPercentage, precision);
    } catch (error) {
      return 0;
    }
  }

  getPromotionName(promotion: any) {
    try {
      let promotionName = promotion.promotionName;
      if (!promotionName) {
        if (promotion?.productExternalCode) {
          promotionName = `PROMOÇÃO POR PRODUTO (${promotion?.productExternalCode})`;
        } else if (promotion?.brandExternalCode) {
          promotionName = `PROMOÇÃO POR MARCA (${promotion?.brandExternalCode})`;
        } else if (promotion?.sectionExternalCode) {
          promotionName = `PROMOÇÃO POR SEÇÃO (${promotion?.sectionExternalCode})`;
        } else if (promotion?.categoryExternalCode) {
          promotionName = `PROMOÇÃO POR CATEGORIA (${promotion?.categoryExternalCode})`;
        } else if (promotion?.departmentExternalCode) {
          promotionName = `PROMOÇÃO POR DEPARTAMENTO (${promotion?.departmentExternalCode})`;
        } else if (promotion?.providerExternalCode) {
          promotionName = `PROMOÇÃO POR FORNCEDOR (${promotion?.providerExternalCode})`;
        } else {
          promotionName = `PROMOÇÃO - DESC. ${promotion?.discountPercentage ?? 0}%`;
        }
      }

      return promotionName;
    } catch (error) {
      return '';
    }
  }

  private getPromotionPrice(
    promotion: PromotionOrderInterface,
    soldPrice: number,
    addFixedPrice = 0,
    precision = 2
  ): number {
    try {
      let promotionPrice = 0;
      if (!promotion?.discountPercentage && !promotion?.fixedPrice) {
        promotionPrice = toDecimals(soldPrice, precision);

        return promotionPrice;
      }

      if (promotion?.fixedPrice) {
        promotionPrice = toDecimals(promotion?.fixedPrice + addFixedPrice, precision);

        return promotionPrice;
      }

      if (promotion?.discountPercentage) {
        promotionPrice = toDecimals(soldPrice - (soldPrice * promotion?.discountPercentage) / 100, precision);

        return promotionPrice;
      }

      return promotionPrice;
    } catch (error) {
      return 0;
    }
  }

  getPromotionRequiredProducts(
    promotionExternalCode: string
  ): Promise<AxiosResponse<RequiredPromotionProduct[] | null>> {
    return api.get(`/promotion/required-products`, {
      params: {
        promotionExternalCode
      }
    });
  }
}

export default new PromotionService();
